import { z } from "zod";

import { DEFAULT_LOCALE, LOCALES } from "@/i18n/types";

function booleanFlag(defaultValue: boolean) {
  return z
    .union([z.boolean(), z.string()])
    .optional()
    .transform((value) => {
      if (value === undefined) return defaultValue;
      if (typeof value === "boolean") return value;
      return value === "true" || value === "1";
    });
}

const envSchema = z.object({
  apiBaseUrl: z.string().default("/api"),
  baseUrl: z.string().default("http://localhost:4174"),
  cloudflareAnalyticsToken: z.string().optional(),
  defaultLocale: z
    .enum(LOCALES as unknown as [string, ...string[]])
    .default(DEFAULT_LOCALE),
  enableAnalytics: booleanFlag(false),
  enableAuthScaffold: booleanFlag(true),
  mode: z.string(),
  pagesProjectName: z.string().optional(),
  plausibleDomain: z.string().optional(),
});

export type AppEnv = z.infer<typeof envSchema>;

function coalesce(value: unknown): unknown {
  if (value === "" || value === undefined) return undefined;
  return value;
}

export function readAppEnv(envSource: ImportMetaEnv = import.meta.env): AppEnv {
  const typedEnv = envSource as Record<string, string | undefined>;

  return envSchema.parse({
    apiBaseUrl: typedEnv.VITE_API_BASE_URL,
    baseUrl: typedEnv.VITE_PUBLIC_BASE_URL,
    cloudflareAnalyticsToken: coalesce(typedEnv.VITE_CLOUDFLARE_ANALYTICS_TOKEN),
    defaultLocale: coalesce(typedEnv.VITE_DEFAULT_LOCALE),
    enableAnalytics: typedEnv.VITE_ENABLE_ANALYTICS,
    enableAuthScaffold: typedEnv.VITE_ENABLE_AUTH,
    mode: typedEnv.MODE,
    pagesProjectName: typedEnv.VITE_CLOUDFLARE_PAGES_PROJECT_NAME,
    plausibleDomain: coalesce(typedEnv.VITE_PLAUSIBLE_DOMAIN),
  });
}
